/**
 * Intro-email copy seam (0161; drawer parity 2026-07-26).
 *
 * The fetch + clipboard half of the CM intro email, deliberately kept OUT of
 * src/lib/introEmail.ts so that module stays pure (no React, no fetch) and
 * unit-testable. TWO surfaces copy the same email and must never drift:
 *
 *   - the People-tab bio/photo editor (IntroEmailSection — copy + preview)
 *   - the work-item drawer's Care manager row (CmBlock — copy only)
 *
 * Both assemble through renderIntroEmail and write both clipboard flavors
 * here, so a template or clipboard change lands on both at once. The drawer
 * row used to write the bare bio text under a "Copy bio" label; routing it
 * through this seam is what makes the label and the payload agree.
 */

import { copyRichToClipboard } from '@/lib/clipboard';
import { renderIntroEmail, type IntroEmailData } from '@/lib/introEmail';

/**
 * Fetch the (already browser-cached) CM photo and re-encode it as a data URI
 * so the copied HTML carries the image bytes inline (Outlook embeds it on
 * paste). null on any failure — the email renders without a photo rather
 * than blocking the copy. Pass the URL from `cmPhotoUrl`.
 */
export async function fetchCmPhotoDataUri(url: string): Promise<string | null> {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Render the intro email from the profile facts + tenant paragraph templates
 * and write BOTH clipboard flavors (text/html for Outlook, text/plain as the
 * fallback). The one place either surface turns profile facts into a copy.
 */
export async function copyIntroEmail(
  data: IntroEmailData,
  paragraphs: readonly string[],
): Promise<void> {
  const { html, text } = renderIntroEmail(data, paragraphs);
  await copyRichToClipboard(html, text);
}
